import {useMemo, useState} from 'react';
import {Table, Pagination, Select, Spin, Button, Tag} from 'antd';
import moment from 'moment';
import {useQuery} from '@apollo/client';
import {GET_LEADERBOARD_SCHEMES} from '../resolvers';
import styles from './userLeaderboard.module.scss';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';

const {Option} = Select;

export const UserLeaderboard = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const navigate = useNavigate();

  const {loading, data} = useQuery(GET_LEADERBOARD_SCHEMES, {
    variables: {
      page,
      limit: pageSize,
      type: 'LEADERBOARD',
    },
    onError: error => toast.error(error?.message),
    fetchPolicy: 'cache-and-network',
  });

  const rewardSchemes = data?.getAllRewardSchemes?.data?.rewardSchemes || [];

  const filteredSchemes = useMemo(() => {
    if (statusFilter === 'ALL') {
      return rewardSchemes;
    }
    const showEnded = statusFilter === 'ENDED';
    return rewardSchemes.filter(
      scheme => Boolean(scheme.isExpired) === showEnded,
    );
  }, [rewardSchemes, statusFilter]);

  const columns = [
    {
      title: 'Competition',
      dataIndex: 'title',
      key: 'title',
      render: text => <span className={styles.title}>{text}</span>,
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) =>
        record.startDate && record.expirationDate
          ? `${moment(record.startDate).format('MMM D, YYYY')} - ${moment(
              record.expirationDate,
            ).format('MMM D, YYYY')}`
          : '-',
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) =>
        record.operationConfig?.cumulativeDurationValue
          ? `${record.operationConfig.cumulativeDurationValue} ${
              record.operationConfig.cumulativeDurationUnit || 'days'
            }`
          : '-',
    },
    {
      title: 'Status',
      dataIndex: 'isExpired',
      key: 'status',
      render: expired =>
        expired ? (
          <Tag color="grey">ENDED</Tag>
        ) : (
          <Tag color="green">ONGOING</Tag>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() =>
            navigate(`/dashboard/leaderboard/scheme/${record.id}`)
          }>
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1>User Leaderboard Schemes</h1>
      <div className={styles.toolbar}>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{width: 200}}>
          <Option value="ALL">All</Option>
          <Option value="ONGOING">Ongoing</Option>
          <Option value="ENDED">Ended</Option>
        </Select>
      </div>
      <Table
        rowKey="id"
        dataSource={filteredSchemes}
        columns={columns}
        pagination={false}
        locale={{emptyText: 'No leaderboards available'}}
      />
      <div className={styles.pagination}>
        <Pagination
          size="small"
          current={page}
          pageSize={pageSize}
          total={data?.getAllRewardSchemes?.pagination?.total || 0}
          showSizeChanger
          onChange={(nextPage, size) => {
            setPage(nextPage);
            if (size !== pageSize) {
              setPageSize(size);
            }
          }}
        />
      </div>
    </div>
  );
};
